import gsap from 'gsap';
import { prefersReducedMotion } from '@theme/utilities';
import {
  getScrollEventTarget,
  getScrollTop,
  scrollContainerMediaQuery,
  scrollTo,
} from '@theme/scroll-container';

const SNAP_DURATION = 0.5;
const MOMENTUM_SUPPRESS_MS = 200;

/**
 * Snaps the content section to the top of the viewport via GSAP transform.
 * Once snapped, the hero is hidden and scroll-up back to the hero is blocked.
 */
class LandingPage extends HTMLElement {
  /** @type {((event: WheelEvent) => void) | undefined} */
  #wheelHandler;

  /** @type {((event: TouchEvent) => void) | undefined} */
  #touchStartHandler;

  /** @type {((event: TouchEvent) => void) | undefined} */
  #touchMoveHandler;

  /** @type {(() => void) | undefined} */
  #resizeHandler;

  /** @type {(() => void) | undefined} */
  #mediaQueryChangeHandler;

  /** @type {((event: MouseEvent) => void) | undefined} */
  #logoClickHandler;

  /** @type {((event: MouseEvent) => void) | undefined} */
  #heroClickHandler;

  /** @type {boolean} */
  #hasSnapped = false;

  /** @type {gsap.core.Timeline | null} */
  #timeline = null;

  /** @type {((event: TouchEvent) => void) | undefined} */
  #touchEndHandler;

  /** @type {boolean} */
  #momentumSuppressed = false;

  /** @type {number | null} */
  #momentumSuppressTimer = null;

  /** @type {number} */
  #touchStartY = 0;

  /** @type {ResizeObserver | undefined} */
  #footerResizeObserver;

  connectedCallback() {
    this.#wheelHandler = (event) => this.#handleWheel(event);
    this.#touchStartHandler = (event) => this.#handleTouchStart(event);
    this.#touchMoveHandler = (event) => this.#handleTouchMove(event);
    this.#resizeHandler = () => this.#handleResize();
    this.#mediaQueryChangeHandler = () => this.#handleResize();
    this.#logoClickHandler = (event) => this.#handleLogoClick(event);
    this.#heroClickHandler = () => this.#playSnapAnimation();
    this.#touchEndHandler = () => this.#handleTouchEnd();

    const scrollTarget = getScrollEventTarget();
    scrollTarget.addEventListener('wheel', this.#wheelHandler, { passive: false });
    scrollTarget.addEventListener('touchstart', this.#touchStartHandler, { passive: true });
    scrollTarget.addEventListener('touchmove', this.#touchMoveHandler, { passive: false });
    scrollTarget.addEventListener('touchend', this.#touchEndHandler, { passive: true });
    scrollTarget.addEventListener('touchcancel', this.#touchEndHandler, { passive: true });
    window.addEventListener('resize', this.#resizeHandler);
    scrollContainerMediaQuery.addEventListener('change', this.#mediaQueryChangeHandler);

    const logoLink = this.querySelector('.landing-page__logo-link');
    if (logoLink instanceof HTMLAnchorElement) {
      logoLink.addEventListener('click', this.#logoClickHandler);
    }

    const hero = this.querySelector('.landing-page__hero');
    if (hero instanceof HTMLElement) {
      hero.addEventListener('click', this.#heroClickHandler);
    }

    this.#observeFooterUtilities();
  }

  disconnectedCallback() {
    this.#timeline?.kill();
    this.#timeline = null;
    this.#clearMomentumSuppression();

    const scrollTarget = getScrollEventTarget();

    if (this.#wheelHandler) {
      scrollTarget.removeEventListener('wheel', this.#wheelHandler);
    }
    if (this.#touchStartHandler) {
      scrollTarget.removeEventListener('touchstart', this.#touchStartHandler);
    }
    if (this.#touchMoveHandler) {
      scrollTarget.removeEventListener('touchmove', this.#touchMoveHandler);
    }
    if (this.#touchEndHandler) {
      scrollTarget.removeEventListener('touchend', this.#touchEndHandler);
      scrollTarget.removeEventListener('touchcancel', this.#touchEndHandler);
    }
    if (this.#resizeHandler) {
      window.removeEventListener('resize', this.#resizeHandler);
    }
    if (this.#mediaQueryChangeHandler) {
      scrollContainerMediaQuery.removeEventListener('change', this.#mediaQueryChangeHandler);
    }

    const logoLink = this.querySelector('.landing-page__logo-link');
    if (logoLink instanceof HTMLAnchorElement && this.#logoClickHandler) {
      logoLink.removeEventListener('click', this.#logoClickHandler);
    }

    const hero = this.querySelector('.landing-page__hero');
    if (hero instanceof HTMLElement && this.#heroClickHandler) {
      hero.removeEventListener('click', this.#heroClickHandler);
    }

    this.#unobserveFooterUtilities();
    this.#clearFooterUtilitiesHeight();
  }

  #getFooterUtilities() {
    const footer = document.querySelector('.footer-utilities');
    return footer instanceof HTMLElement ? footer : null;
  }

  #updateFooterUtilitiesHeight() {
    const footer = this.#getFooterUtilities();
    const height = footer?.offsetHeight ?? 0;
    document.documentElement.style.setProperty('--footer-utilities-height', `${height}px`);
  }

  #clearFooterUtilitiesHeight() {
    document.documentElement.style.removeProperty('--footer-utilities-height');
  }

  #observeFooterUtilities() {
    const footer = this.#getFooterUtilities();
    if (!footer || this.#footerResizeObserver) return;

    this.#footerResizeObserver = new ResizeObserver(() => {
      this.#updateFooterUtilitiesHeight();
    });
    this.#footerResizeObserver.observe(footer);
    this.#updateFooterUtilitiesHeight();
  }

  #unobserveFooterUtilities() {
    this.#footerResizeObserver?.disconnect();
    this.#footerResizeObserver = undefined;
  }

  #getHero() {
    const hero = this.querySelector('.landing-page__hero');
    return hero instanceof HTMLElement ? hero : null;
  }

  #getContent() {
    const content = this.querySelector('landing-page-content');
    return content instanceof HTMLElement ? content : null;
  }

  #getSnapDistance() {
    const hero = this.#getHero();
    return hero?.offsetHeight ?? 0;
  }

  #usesDesktopSnapLayout() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  #getSnapMotionTargets() {
    const content = this.#getContent();
    if (!content) return [];

    if (!this.#usesDesktopSnapLayout()) return [content];

    const footer = this.#getFooterUtilities();
    return footer ? [content, footer] : [content];
  }

  #lockLogoBarWidth() {
    if (!this.#usesDesktopSnapLayout()) return;

    const logo = this.querySelector('.landing-page__logo');
    if (!(logo instanceof HTMLElement)) return;

    const { width } = logo.getBoundingClientRect();
    logo.style.setProperty('--logo-fixed-width', `${width}px`);
    logo.style.width = `${width}px`;
    logo.style.maxWidth = `${width}px`;
  }

  #clearLogoBarWidth() {
    const logo = this.querySelector('.landing-page__logo');
    if (!(logo instanceof HTMLElement)) return;

    logo.style.removeProperty('--logo-fixed-width');
    logo.style.removeProperty('width');
    logo.style.removeProperty('max-width');
  }

  #reserveMobileLogoBarSpace() {
    if (window.matchMedia('(min-width: 750px)').matches) return;

    const logoBar = this.querySelector('.landing-page__logo-bar');
    if (!(logoBar instanceof HTMLElement)) return;

    this.style.setProperty('--landing-logo-bar-height', `${logoBar.offsetHeight}px`);
  }

  #clearMobileLogoBarSpace() {
    this.style.removeProperty('--landing-logo-bar-height');
  }

  #resetToHero() {
    this.#timeline?.kill();
    this.#timeline = null;
    this.#clearMomentumSuppression();

    const hero = this.#getHero();
    const motionTargets = this.#getSnapMotionTargets();

    if (hero && motionTargets.length) {
      gsap.killTweensOf([hero, ...motionTargets]);
      gsap.set(hero, { clearProps: 'all' });
      gsap.set(motionTargets, { clearProps: 'all' });
    }

    this.#clearLogoBarWidth();
    this.#clearMobileLogoBarSpace();

    this.#hasSnapped = false;
    this.classList.remove('landing-page--snapped', 'landing-page--animating');
    scrollTo({ top: 0, behavior: 'instant' });
  }

  #handleLogoClick(event) {
    const link = event.currentTarget;
    if (!(link instanceof HTMLAnchorElement)) return;

    const targetUrl = new URL(link.href);
    const isHome =
      targetUrl.origin === window.location.origin &&
      targetUrl.pathname === window.location.pathname;

    if (isHome) {
      event.preventDefault();
      this.#resetToHero();
    }
  }

  #finalizeSnap() {
    const content = this.#getContent();
    const footer = this.#getFooterUtilities();

    this.#reserveMobileLogoBarSpace();

    this.#hasSnapped = true;
    this.classList.remove('landing-page--animating');
    this.classList.add('landing-page--snapped');

    this.#lockLogoBarWidth();

    if (content) {
      gsap.set(content, { clearProps: 'transform' });
    }

    if (footer) {
      gsap.set(footer, { clearProps: 'transform' });
    }

    scrollTo({ top: 0, behavior: 'instant' });
    this.#timeline = null;
  }

  #playSnapAnimation() {
    if (this.#hasSnapped || this.#timeline?.isActive()) return;

    const hero = this.#getHero();
    const content = this.#getContent();
    if (!hero || !content) return;

    const distance = this.#getSnapDistance();
    if (distance <= 0) return;

    const motionTargets = this.#getSnapMotionTargets();

    this.#updateFooterUtilitiesHeight();
    this.classList.add('landing-page--animating');

    if (prefersReducedMotion()) {
      gsap.set(motionTargets, { y: -distance });
      gsap.set(hero, { autoAlpha: 0 });
      this.#finalizeSnap();
      return;
    }

    this.#timeline = gsap.timeline({
      defaults: { ease: 'power3.out' },
      onComplete: () => this.#finalizeSnap(),
    });

    this.#timeline
      .to(motionTargets, { y: -distance, duration: SNAP_DURATION }, 0)
      .to(hero, { autoAlpha: 0, duration: 0.4 }, 0.1);
  }

  #clearMomentumSuppression() {
    if (this.#momentumSuppressTimer != null) {
      clearTimeout(this.#momentumSuppressTimer);
      this.#momentumSuppressTimer = null;
    }
    this.#momentumSuppressed = false;
  }

  #beginMomentumSuppression() {
    this.#momentumSuppressed = true;
  }

  #extendMomentumSuppression() {
    this.#beginMomentumSuppression();

    if (this.#momentumSuppressTimer != null) {
      clearTimeout(this.#momentumSuppressTimer);
    }

    this.#momentumSuppressTimer = window.setTimeout(() => {
      this.#momentumSuppressTimer = null;
      this.#momentumSuppressed = false;
    }, MOMENTUM_SUPPRESS_MS);
  }

  #shouldBlockScrollUp() {
    return this.#hasSnapped && getScrollTop() <= 2;
  }

  #handleWheel(event) {
    if (this.#momentumSuppressed) {
      event.preventDefault();
      this.#extendMomentumSuppression();
      return;
    }

    if (this.#timeline?.isActive()) {
      event.preventDefault();
      this.#extendMomentumSuppression();
      return;
    }

    if (this.#hasSnapped) {
      if (this.#shouldBlockScrollUp() && event.deltaY < 0) {
        event.preventDefault();
      }
      return;
    }

    if (event.deltaY <= 0) return;

    event.preventDefault();
    this.#beginMomentumSuppression();
    this.#extendMomentumSuppression();
    this.#playSnapAnimation();
  }

  #handleTouchStart(event) {
    if (event.touches.length !== 1) return;
    this.#touchStartY = event.touches[0].clientY;
  }

  #handleTouchMove(event) {
    if (event.touches.length !== 1) return;

    if (this.#momentumSuppressed) {
      event.preventDefault();
      return;
    }

    if (this.#timeline?.isActive()) {
      event.preventDefault();
      return;
    }

    if (this.#shouldBlockScrollUp() && event.touches[0].clientY > this.#touchStartY) {
      event.preventDefault();
      return;
    }

    if (this.#hasSnapped) return;

    if (event.touches[0].clientY < this.#touchStartY) {
      event.preventDefault();
      this.#beginMomentumSuppression();
      this.#playSnapAnimation();
    }
  }

  #handleTouchEnd() {
    if (!this.#momentumSuppressed) return;
    this.#extendMomentumSuppression();
  }

  #handleResize() {
    if (this.#hasSnapped) {
      this.#updateFooterUtilitiesHeight();
      return;
    }

    if (this.#timeline?.isActive()) return;

    const content = this.#getContent();
    const footer = this.#getFooterUtilities();

    if (content) {
      gsap.set(content, { clearProps: 'transform' });
    }

    if (footer) {
      gsap.set(footer, { clearProps: 'transform' });
    }

    const hero = this.#getHero();
    if (hero) {
      gsap.set(hero, { clearProps: 'autoAlpha' });
    }
  }
}

/**
 * Crossfades gallery images with optional autoplay.
 */
class LandingPageGallery extends HTMLElement {
  /** @type {number | undefined} */
  #interval;

  connectedCallback() {
    const slides = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.landing-page__gallery-slide')]);

    if (!slides.length) return;

    slides.forEach((slide, index) => {
      slide.classList.toggle('landing-page__gallery-slide--active', index === 0);
    });

    if (slides.length <= 1) return;

    const speed = parseInt(this.dataset.autoplay || '', 10);
    if (!speed || prefersReducedMotion()) return;

    let current = 0;

    this.#interval = window.setInterval(() => {
      slides[current]?.classList.remove('landing-page__gallery-slide--active');
      current = (current + 1) % slides.length;
      slides[current]?.classList.add('landing-page__gallery-slide--active');
    }, speed * 1000);
  }

  disconnectedCallback() {
    if (this.#interval) {
      clearInterval(this.#interval);
    }
  }
}

if (!customElements.get('landing-page')) {
  customElements.define('landing-page', LandingPage);
}

if (!customElements.get('landing-page-gallery')) {
  customElements.define('landing-page-gallery', LandingPageGallery);
}
